import React, { useEffect, useState } from 'react';
import { Button, ConfirmModal, ProgressBar } from '@quyva/shared-ui';
import {
    faBell,
    faCancel,
    faClose,
    faFileExport,
    faFileUpload,
    faFolderPlus,
    faExclamationTriangle,
    faCircleXmark,
    faCircleCheck,
    faProjectDiagram,
    faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
    ProcessType,
    TaskProgressType,
    getDisplayDateTimeParts,
    handleHtmlStringToElement,
} from '@quyva/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NotificationsColors } from '../../styles/notificationsColors';

// Status structure
//{
// message: 'Processing!';
// percentageCompletion: 60;
// process: 'file upload';
// progress: 'processing';
// repoId: '3';
// repoName: 'Gama Repo';
// statusCode: 200;
// timeStamp: '2024-05-14 21:46:23';
// userMail: 'shubham.singh@mtree.co.in';
//}

export const SingleNotificationsView = ({ socket, notification, key }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSlid, setIsSlid] = useState(false);

    // Date time formatting
    const { date: date, time: time } = getDisplayDateTimeParts(
        notification.timeStamp
    );

    // Notification status formatting
    let status = '';
    let iconColor = NotificationsColors.SUCCESS;
    let textIcon = faCircleCheck;
    if (
        notification.progress === TaskProgressType.PROCESSING ||
        notification.progress === TaskProgressType.STARTED
    ) {
        status = 'active';
        iconColor = NotificationsColors.ONGOING;
        textIcon = null;
    } else if (notification.progress === TaskProgressType.FAILED) {
        status = 'exception';
        iconColor = NotificationsColors.FAIL;
        textIcon = faCircleXmark;
    } else if (notification.progress === TaskProgressType.CANCELLING) {
        status = 'active';
        iconColor = NotificationsColors.CANCELLING;
        textIcon = faCancel;
    } else if (notification.progress === TaskProgressType.CANCELLED) {
        status = 'exception';
        iconColor = NotificationsColors.CANCELLED;
        textIcon = faCancel;
    } else if (notification.progress === TaskProgressType.WARNING) {
        iconColor = NotificationsColors.WARNING;
        textIcon = faExclamationTriangle;
    }

    // Notification icon formatting
    let icon = faBell;
    if (
        notification.process === ProcessType.EXTRACTION ||
        notification.process === ProcessType.SupportInfoExtraction
    ) {
        icon = faFileExport;
    } else if (notification.process === ProcessType.FILE_UPLOAD) {
        icon = faFileUpload;
    } else if (notification.process === ProcessType.REPO_CREATION) {
        icon = faFolderPlus;
    } else if (notification.process === ProcessType.KNOWLEDGE_GRAPH_CREATION) {
        icon = faProjectDiagram;
    } else if (notification.process === ProcessType.ALERT) {
        icon = faExclamationTriangle;
        // For ALERT notifications, always show warning color
        iconColor = NotificationsColors.WARNING;
        textIcon = faExclamationTriangle;
    }
    const isAsyncExecution =
        notification.process === ProcessType.ASYNC_AGENT_EXECUTION;
    const isAlert = notification.process === ProcessType.ALERT;

    const isCompleted = notification.progress === TaskProgressType.COMPLETED;

    const rawOutput = notification.output_text;
    const rawFiles = notification.output_files;

    let parsedOutputText = null;
    try {
        if (rawOutput) {
            parsedOutputText =
                typeof rawOutput === 'object'
                    ? rawOutput
                    : JSON.parse(rawOutput);
        }
    } catch (e) {
        parsedOutputText = {
            response:
                'Unable to display agent output. Please try again or contact support.',
        };
    }

    return (
        <div
            key={key}
            className={`w-full min-h-16 flex flex-col md:flex-row items-start md:items-center p-2 text-texts-secondary300 border shadow-sm rounded-xl mb-1.5 bg-backgrounds-white`}
        >
            {!isSlid && (
                <>
                    <div className='h-full w-full md:w-1/6 flex justify-start md:justify-center items-center'>
                        <FontAwesomeIcon
                            icon={icon}
                            color={iconColor}
                            size='2x'
                            className='drop-shadow'
                        />
                    </div>
                    <div className='w-full md:w-5/6 pr-2'>
                        <div className='w-full flex flex-col md:flex-row justify-between'>
                            <div
                                className='w-1/2 font-semibold truncate'
                                title={notification.repoName}
                            >
                                {notification.repoName}
                            </div>
                            <div className='w-1/2 flex justify-start md:justify-end items-center text-xs mt-1 md:mt-0'>
                                <div className='keep-all pr-1 border-r'>
                                    {date}
                                </div>
                                <div className='keep-all pl-1'>{time}</div>
                            </div>
                        </div>

                        {!showDetails ? (
                            <div className='w-full flex justify-between mt-2 md:mt-0'>
                                <div className='capitalize'>
                                    {isAlert ? (
                                        // For ALERT, show brief message directly
                                        <span className='text-sm'>{notification.message}</span>
                                    ) : (
                                        <>
                                            {`${notification.process} ${notification.progress}`}
                                            {textIcon && (
                                                <FontAwesomeIcon
                                                    color={iconColor}
                                                    icon={textIcon}
                                                    className='ml-1'
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                {!isAlert && isAsyncExecution && isCompleted ? (
                                    <div
                                        className='cursor-pointer text-primary font-semibold'
                                        onClick={(e) => {
                                            setIsSlid(true);
                                            e.stopPropagation();
                                        }}
                                    >
                                        View Output
                                    </div>
                                ) : !isAlert ? (
                                    <div
                                        className='cursor-pointer text-texts-tertiary font-semibold'
                                        onClick={(e) => {
                                            setShowDetails(true);
                                            e.stopPropagation();
                                        }}
                                    >
                                        Details
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className='w-full flex justify-between items-start mt-2 md:mt-0 break-words'>
                                <div>{notification.message}</div>
                                <div
                                    className='cursor-pointer text-texts-tertiary font-semibold'
                                    onClick={(e) => {
                                        setShowDetails(false);
                                        e.stopPropagation();
                                    }}
                                >
                                    Hide
                                </div>
                            </div>
                        )}

                        {notification.progress &&
                            notification.progress.toLowerCase() ===
                                TaskProgressType.PROCESSING && (
                                <div className='flex items-center justify-between mt-2 md:mt-0'>
                                    {notification.percentageCompletion && (
                                        <ProgressBar
                                            percentage={
                                                notification.percentageCompletion
                                            }
                                            status={status}
                                            size='small'
                                            allowStrokeColor={false}
                                        />
                                    )}

                                    {!notification.percentageCompletion && (
                                        <div />
                                    )}

                                    {!isAsyncExecution && (
                                        <Button
                                            className='ml-1 w-[24px] h-[24px]'
                                            onClick={() =>
                                                setShowConfirmModal(true)
                                            }
                                            hoverText={`Cancel ${notification.process} on ${notification.repoName}`}
                                            icon={faClose}
                                            type='danger'
                                        />
                                    )}
                                </div>
                            )}
                    </div>
                </>
            )}

            {isSlid && (
                <div className='w-full pl-2 pr-2'>
                    <div className='w-full flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2 min-w-0'>
                            <FontAwesomeIcon
                                icon={icon}
                                color={iconColor}
                                size='sm'
                            />
                            <div
                                className='font-semibold truncate'
                                title={notification.repoName}
                            >
                                {notification.repoName}
                            </div>
                        </div>
                        <div className='flex items-center text-xs whitespace-nowrap'>
                            <div className='pr-1 border-r'>{date}</div>
                            <div className='pl-1'>{time}</div>
                        </div>
                    </div>

                    <div className='w-full flex justify-between mt-2 md:mt-0'>
                        <div className='capitalize font-semibold text-texts-secondary300'>
                            Agent Output
                        </div>

                        <div
                            className='cursor-pointer text-primary font-semibold'
                            onClick={(e) => {
                                setIsSlid(false);
                                e.stopPropagation();
                            }}
                        >
                            Back
                        </div>
                    </div>

                    <div className=' text-texts-secondary300 break-words'>
                        {parsedOutputText?.response ? (
                            handleHtmlStringToElement(parsedOutputText.response)
                        ) : (
                            <div>No output text available.</div>
                        )}
                    </div>

                    {rawFiles?.length > 0 && (
                        <div className='mt-2'>
                            <div className='font-semibold text-xs mb-1'>
                                Output Files
                            </div>
                            <ul className='list-disc ml-4'>
                                {rawFiles.map((file, idx) => (
                                    <li key={idx}>{file}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {showConfirmModal && (
                <ConfirmModal
                    modalText={`Are you sure you want to cancel ${notification.process} on ${notification.repoName}?`}
                    onConfirm={() => {
                        socket.emit('cancelTask', {
                            userId: notification.userId,
                            repoId: notification.repoId,
                        });
                        setShowConfirmModal(false);
                    }}
                    onCancel={() => {
                        setShowConfirmModal(false);
                    }}
                />
            )}
        </div>
    );
};
